import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { isNullish } from '@sas/nova-core/typeguards';
import cx from 'classnames';
import { useConfig } from '../../hooks/useConfig';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useMounted } from '../../hooks/useMounted';
import { useLanguage } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Label } from '../Label';
import { Flow } from '../Layouts/Flow';
import { TabBar, TabBarVariant } from '../TabBar';
import { BasicColorPicker, CustomColorPicker, recentStandardColors, setRecentStandardColors, useColorPickerDimensions } from './__internal__/';
import { useColorPickerService } from './ColorPickerProvider';
import bundle from './NovaComponents-ColorPicker-gui-icu.properties';
import cn from './DefaultColorPicker.css.json';
import './DefaultColorPicker.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useHtmlId = createUseHtmlId('DefaultColorPicker');
const logger = getLogger('DefaultColorPicker');
export const DefaultColorPicker = /*#__PURE__*/forwardRef(({
  hideCustomTabActionButtons = false,
  ...props
}, ref) => {
  const localize = useTextLocalizer();
  const newSwatchColorRef = useRef(props.newColor);
  const setNewSwatchColor = useCallback(newColor => {
    newSwatchColorRef.current = newColor;
  }, []);
  const lastSelectedColor = props.lastSelectedColor ?? {
    color: '#000000',
    type: 'standard'
  };
  const currentColor = props.selectedColor;
  const newColor = hideCustomTabActionButtons ? props.newColor : newSwatchColorRef.current;
  const [basicTabPanelId, customTabPanelId] = useHtmlId(2);
  const tabItems = [{
    label: localize('basicTab.label'),
    ariaControls: basicTabPanelId
  }, {
    label: localize('customTab.label'),
    ariaControls: customTabPanelId
  }];
  const dt = useDesignTokens();
  const lang = useLanguage();
  const locale = lang.toLowerCase();
  const getLanguageHeight = () => {
    switch (locale) {
      case 'zh-cn-pseudo':
        if (props.numberOfRecentCustomRows && props.numberOfRecentCustomRows >= 2) {
          return hideCustomTabActionButtons ? '25rem' : '30rem';
        }
      default:
        return hideCustomTabActionButtons ? '25rem' : '28rem';
    }
  };
  const tokens = renderInlineCssVars({
    pd: dt.tokens['space-base-m'],
    height: getLanguageHeight()
  }, 'color-picker');
  const appId = useConfig()?.get('appId');
  const service = props.dataProvider ?? useColorPickerService();
  const storeRecentCustomColorsPerApplication = props.storeRecentCustomColorsPerApplication ?? true;
  const [recentCustomColors, setRecentCustomColors] = useState([]);
  const mounted = useMounted();
  const onBasicTabColorSelect = color => {
    props.onColorSelect(color);
    if (!hideCustomTabActionButtons && color.type === 'standard' && color.color !== '#000000') {
      persistStandardColor(color);
    }
  };
  const persistStandardColor = color => {
    if (!recentStandardColors.includes(color.color)) {
      setRecentStandardColors([color.color, ...recentStandardColors].slice(0, 7));
    }
  };
  const persistCustomColor = async color => {
    const selectedColorIndex = recentCustomColors.indexOf(color.color);
    let recentColors = recentCustomColors.slice(0, 20);
    if (selectedColorIndex === -1 || selectedColorIndex >= (props.numberOfRecentCustomRows ?? 1) * 7) {
      if (selectedColorIndex >= (props.numberOfRecentCustomRows ?? 1) * 7) {
        recentColors.splice(selectedColorIndex, 1);
      }
      recentColors = [color.color, ...recentColors];
      try {
        await service?.setRecentCustomColors(recentColors, storeRecentCustomColorsPerApplication ? appId : null);
        // state update incase this component is used outside ColorPicker
        if (mounted.current) setRecentCustomColors(recentColors);
      } catch (error) {
        logger.error(`The call to set recent custom colors ${storeRecentCustomColorsPerApplication ? 'using '.concat(appId) : ''} has failed`);
      }
    }
  };
  const onCustomTabColorSelect = color => {
    if (!hideCustomTabActionButtons && color.color !== '#000000') {
      if (color.type === 'standard') {
        persistStandardColor(color);
      } else {
        persistCustomColor(color);
      }
    }
    props.onColorSelect(color);
  };
  useEffect(() => {
    let mounted = true;
    const fetchRecentCustomColors = async () => {
      if (!storeRecentCustomColorsPerApplication && service) {
        try {
          const storedCustomColors = await service.getRecentCustomColors();
          if (mounted && !isNullish(storedCustomColors)) {
            setRecentCustomColors(storedCustomColors);
          }
        } catch (error) {
          logger.error(`The call to get recent custom colors per user across applications has failed`);
        }
      }
    };
    fetchRecentCustomColors();
    return () => {
      mounted = false;
    };
  }, [service, storeRecentCustomColorsPerApplication]);
  useEffect(() => {
    let mounted = true;
    const fetchRecentCustomColors = async () => {
      if (service && storeRecentCustomColorsPerApplication && appId) {
        try {
          const storedCustomColors = await service.getRecentCustomColors(appId);
          if (mounted && !isNullish(storedCustomColors)) {
            setRecentCustomColors(storedCustomColors);
          }
        } catch (error) {
          logger.error(`The call to get recent custom colors for application ${appId} has failed`);
        }
      }
    };
    fetchRecentCustomColors();
    return () => {
      mounted = false;
    };
  }, [appId, service, storeRecentCustomColorsPerApplication]);
  const {
    containerWidth,
    calculateDimensions,
    labelContainerRef: invisibleLabelContainerRef
  } = useColorPickerDimensions();
  return /*#__PURE__*/_jsxs(Flow, {
    ref: container => {
      setRef(ref, container);
      calculateDimensions();
    },
    style: {
      ...tokens,
      ...props.style,
      width: containerWidth
    },
    orientation: "vertical",
    "data-testid": props.testId,
    className: cx(cn.defaultColorPickerContainer, props.className),
    children: [/*#__PURE__*/_jsx(Flow, {
      "data-testid": postfixTestId(props.testId, 'tabbarContainer'),
      className: cn.tabbarContainer,
      spacing: "s",
      children: /*#__PURE__*/_jsx(TabBar, {
        testId: postfixTestId(props.testId, 'tabbar'),
        stretchToFit: true,
        variant: TabBarVariant.Standard,
        visualStyle: 'standard-small',
        selectedIndex: props.selectedIndex,
        onItemSelected: ({
          index
        }) => {
          props.onTabItemSelect(index);
        },
        items: tabItems
      })
    }), props.selectedIndex === 0 ? /*#__PURE__*/_jsx(BasicColorPicker, {
      id: basicTabPanelId,
      testId: postfixTestId(props.testId, 'basicColorPicker'),
      onColorSelect: onBasicTabColorSelect,
      renderAutomaticSwatch: props.renderAutomaticSwatch,
      selectedColor: currentColor,
      newColor: newColor,
      lastSelectedColor: lastSelectedColor,
      automaticColor: props.automaticColor,
      hideNoColor: props.hideNoColor,
      showColorIndicator: props.showColorIndicator ?? hideCustomTabActionButtons,
      recentCustomColors: recentCustomColors,
      numberOfRecentCustomRows: props.numberOfRecentCustomRows,
      hideAutomaticColor: props.hideAutomaticColor
    }) : /*#__PURE__*/_jsx(CustomColorPicker, {
      id: customTabPanelId,
      testId: postfixTestId(props.testId, 'customColorPicker'),
      selectedColor: currentColor,
      renderAutomaticSwatch: props.renderAutomaticSwatch,
      newColor: newColor,
      lastSelectedColor: lastSelectedColor,
      onColorSelect: onCustomTabColorSelect,
      hideCustomTabActionButtons: hideCustomTabActionButtons,
      ...(!hideCustomTabActionButtons && {
        onCancelPress: props.onCancelPress,
        onNewColorChange: setNewSwatchColor
      })
    }), /*#__PURE__*/_jsx(Label, {
      "aria-hidden": true,
      containerRef: invisibleLabelContainerRef,
      className: cn.invisibleLabel,
      testId: postfixTestId(props.testId, 'invisible-label'),
      children: localize('customTab.greenField.label')
    })]
  });
});
//# sourceMappingURL=DefaultColorPicker.js.map